<template>
  <header
    class="desktop-header"
    :class="{ 'desktop-header--assoc': isEmployee && isCustomer }"
    role="banner"
    id="desktop-header-v2"
    @click.capture="onHeaderClickCapture"
  >
    <WCEmployeeAssociationBanner v-if="isEmployee && isCustomer" />
    <section>
      <div><WCActiveOrderTypeBadgeModern /></div>
    </section>
    <section class="desktop-header__content container-xl">
      <div class="desktop-header__row">
        <WCHamburgerMenu @showMenu="toggleSideMenu" />

        <div class="desktop-header__brand" v-if="isNotAnEmployee">
          <router-link to="/" v-if="!isCustomerModeScan">
            <WCImage
              :imageSrc="brandImageSrc"
              :imageAlt="getStoreName"
              :imageClass="brandImageClass"
              :defaultImage="false"
              data-testid="desktop-header-v2-brand"
              @imgLoadError="onBrandImageError"
            />
          </router-link>
          <div v-else>
            <WCImage
              :imageSrc="brandImageSrc"
              :imageAlt="getStoreName"
              :imageClass="brandImageClass"
              :defaultImage="false"
              @imgLoadError="onBrandImageError"
            />
          </div>
        </div>
        <div class="desktop-header__brand" v-else>
          <WCImage
            :imageSrc="brandImageSrc"
            :imageAlt="getStoreName"
            :imageClass="brandImageClass"
            :defaultImage="false"
            @imgLoadError="onBrandImageError"
          />
        </div>

        <button
          v-if="isNotAnEmployee"
          class="unstyled-btn desktop-header__store"
          type="button"
          data-testid="desktop-header-v2-store-pill"
          :aria-label="$t('selectStore')"
          @click="goToSelectStore"
        >
          <WCSvgIcon name="location-marker" class="desktop-header__store-icon" />
          <span class="desktop-header__store-label">
            {{ getStoreName }}
          </span>
          <WCSvgIcon name="chevron-down" class="desktop-header__chevron" />
        </button>

        <div class="desktop-header__search">
          <WCSearchBarV2
            v-if="
              $route.name !== giftCardRouteName &&
                isNotAnEmployee &&
                $configuration.itemSearchEnabled
            "
            @search="search"
            @fetchSuggestions="fetchSuggestions"
            :suggestions="suggestions"
            :itemGroups="itemGroups"
          />
        </div>

        <div class="desktop-header__account">
          <WCLoginMenuV2 v-if="isGuest && !isEmployee" />
          <button
            v-else
            class="unstyled-btn desktop-header__profile"
            :class="{ 'desktop-header__profile--assoc': isEmployee && isCustomer }"
            type="button"
            :aria-label="$t('accountSettings')"
            data-testid="desktop-header-v2-profile"
            @click="goToAccountSettings"
          >
            <WCSvgIcon name="user" class="desktop-header__profile-icon" />
            <WCUserGreetingV2 :layout="userGreetingLayout.HEADER" />
          </button>
        </div>

        <button
          v-if="
            isNotAnEmployee &&
              ($configuration.cartEnabled || isCustomerModeScan) &&
              !shouldHideItemPrices
          "
          class="unstyled-btn desktop-header__cart"
          type="button"
          :aria-label="$t('shoppingCartWithItemsCount', { count: cartItemCount })"
          @click="toggleCartDrawer"
          id="desktopHeaderCartButton"
        >
          <WCSpinner v-if="isSyncing" color="primary" size="small" />
          <WCSvgIcon
            v-else
            name="shopping-cart"
            class="desktop-header__cart-icon"
            :icon-size="cartItemCount === 0 ? 'xl' : null"
          />
          <span v-if="cartItemCount > 0" class="desktop-header__cart-count">
            {{ cartItemCountLabel }}
          </span>
        </button>
      </div>
    </section>
    <WCNavBar :wcHeaderHeight="wcHeaderHeight" />
  </header>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { markRaw } from 'vue';
import UserMixin from '@/modules/user/mixins/UserMixin';
import EmployeeMixin from '@/modules/employee/mixins/EmployeeMixin';
import CartItemControls from '@/modules/cart/mixins/CartControls/CartItemControls';
import HidePricingMixin from '@/mixins/HidePricingMixin';
import SideMenuService from '@/modules/sidebar/services/SideMenuService';
import emitter from '@/eventBus';
import { SIDE_MENU_EVENTS } from '@/constants/EventConstants';
import WCHamburgerMenu from '@/components/WCHamburgerMenu/WCHamburgerMenu.vue';
import WCImage from '@/components/WCImage/WCImage.vue';
import WCNavBar from '@/components/WCNavBar/WCNavBar.vue';
import WCLoginMenuV2 from '@/components/WCLoginMenuV2/WCLoginMenuV2.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCUserGreetingV2 from '@/components/WCUserGreetingV2/WCUserGreetingV2.vue';
import WCEmployeeAssociationBanner from '@/modules/employee/components/WCEmployeeAssociationBanner/WCEmployeeAssociationBanner.vue';
import WCActiveOrderTypeBadgeModern from '@/modules/orderTypes/components/WCActiveOrderTypeBadge/WCActiveOrderTypeBadgeModern.vue';
import { GIFT_CARD_ROUTE_NAME, USER_GREETING_LAYOUT } from '@/constants/AppConstants';
import WCSearchBarV2 from '../WCSearchBarV2/WCSearchBarV2.vue';

export default {
  name: 'WCDesktopHeaderV2',
  mixins: [UserMixin, EmployeeMixin, CartItemControls, HidePricingMixin],
  components: {
    WCHamburgerMenu,
    WCImage,
    WCNavBar,
    WCLoginMenuV2,
    WCSearchBarV2,
    WCSpinner,
    WCUserGreetingV2,
    WCEmployeeAssociationBanner,
    WCActiveOrderTypeBadgeModern,
  },
  data() {
    return {
      userGreetingLayout: USER_GREETING_LAYOUT,
      brandImageSrc: '/api/img/favicon?w=96&h=96',
      isUsingFaviconImage: true,
      hasTriedStoreImageFallback: false,
      headerResizeObserver: null,
      isSideMenuOpen: false,
      wcHeaderHeight: '0px',
    };
  },
  created() {
    this.brandImageSrc = this.getFaviconSrc;
    this.updateHomeStore();
    this.onSidebarOpen = () => {
      this.setSideMenuOpen(true);
    };
    this.onSidebarClose = () => {
      this.setSideMenuOpen(false);
    };
    this.onSidebarToggle = () => {
      this.setSideMenuOpen(!this.isSideMenuOpen);
    };
    emitter.$on(SIDE_MENU_EVENTS.OPEN, this.onSidebarOpen);
    emitter.$on(SIDE_MENU_EVENTS.CLOSE, this.onSidebarClose);
    emitter.$on(SIDE_MENU_EVENTS.TOGGLE, this.onSidebarToggle);
  },
  mounted() {
    this.updateHeaderHeightCssVar();
    this.observeHeaderHeight();
  },
  unmounted() {
    this.disconnectHeaderHeightObserver();
    document.documentElement.style.setProperty('--mobile-header-height', '0px');
    emitter.$off(SIDE_MENU_EVENTS.OPEN, this.onSidebarOpen);
    emitter.$off(SIDE_MENU_EVENTS.CLOSE, this.onSidebarClose);
    emitter.$off(SIDE_MENU_EVENTS.TOGGLE, this.onSidebarToggle);
    document.body.style.overflow = '';
  },
  computed: {
    ...mapGetters({
      suggestions: 'browse/getSuggestions',
      isSyncing: 'cart/isSyncing',
      carts: 'cart/nonEmptyCarts',
      isCustomerModeScan: 'user/isCustomerModeScan',
    }),
    getFaviconSrc() {
      return '/api/img/favicon?w=96&h=96';
    },
    getStoreImageSrc() {
      return `${this.$configuration.cdn}/api/img/${this.$configuration.store.image}`;
    },
    getStoreName() {
      return this.$configuration.store.name;
    },
    isMultiStore() {
      return this.$configuration.multistore;
    },
    brandImageClass() {
      return this.isUsingFaviconImage
        ? 'logo desktop-header__brand-image desktop-header__brand-image--favicon'
        : 'logo desktop-header__brand-image desktop-header__brand-image--logo';
    },
    giftCardRouteName() {
      return GIFT_CARD_ROUTE_NAME;
    },
    itemGroups() {
      return this.$configuration.mainMenu?.children;
    },
    cartItemCount() {
      if (this.$configuration.orderTypesEnabled && !this.isCustomerModeScan) {
        let itemsCount = 0;
        this.carts.forEach(cart => {
          itemsCount += this.cartLineItemCount(cart);
        });
        return itemsCount;
      }
      return this.cartLineItemCount();
    },
    cartItemCountLabel() {
      return `${this.cartItemCount} ${this.cartItemCount === 1 ? 'Item' : 'Items'}`;
    },
  },
  watch: {
    $route() {
      if (this.isSideMenuOpen) {
        this.setSideMenuOpen(false);
      }
    },
  },
  methods: {
    ...mapActions({ fetchSuggestions: 'browse/fetchSuggestions' }),
    toggleSideMenu() {
      SideMenuService.toggle();
    },
    setSideMenuOpen(isOpen) {
      this.isSideMenuOpen = isOpen;
      document.body.style.overflow = isOpen ? 'hidden' : '';
      if (isOpen) {
        emitter.$emit('cartDrawer:close');
      }
    },
    onHeaderClickCapture(event) {
      if (!this.isSideMenuOpen) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      SideMenuService.close();
    },
    onBrandImageError() {
      if (!this.hasTriedStoreImageFallback && this.getStoreImageSrc) {
        this.hasTriedStoreImageFallback = true;
        this.isUsingFaviconImage = false;
        this.brandImageSrc = this.getStoreImageSrc;
      }
    },
    updateHomeStore() {
      if (!this.isMultiStore) {
        this.setUserHomeStore(this.$configuration.store.id);
      }
    },
    search({ term, department, itemGroup }) {
      this.$router.push({
        name: 'Browse',
        query: { q: term, g: itemGroup, ...(department ? { Dept: [department] } : undefined) },
      });
    },
    toggleCartDrawer() {
      this.emitter.$emit('cartDrawer:toggle');
    },
    goToAccountSettings() {
      this.$router.push({ name: 'Account Settings' }).catch(() => {});
    },
    goToSelectStore() {
      this.$router.push({ name: 'SelectStore' }).catch(() => {});
    },
    updateHeaderHeightCssVar() {
      const headerHeight =
        this.$el?.getBoundingClientRect?.().height || this.$el?.offsetHeight || 0;
      document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
      this.wcHeaderHeight = `${headerHeight}px`;
    },

    observeHeaderHeight() {
      if (typeof ResizeObserver !== 'function' || !this.$el) {
        return;
      }

      this.disconnectHeaderHeightObserver();
      const resizeObserver = new ResizeObserver(() => {
        this.updateHeaderHeightCssVar();
      });

      if (!resizeObserver || typeof resizeObserver.observe !== 'function') {
        return;
      }

      this.headerResizeObserver = markRaw(resizeObserver);
      this.headerResizeObserver.observe(this.$el);
    },

    disconnectHeaderHeightObserver() {
      const resizeObserver = this.headerResizeObserver;
      this.headerResizeObserver = null;

      if (!resizeObserver || typeof resizeObserver.disconnect !== 'function') {
        return;
      }

      resizeObserver.disconnect();
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.desktop-header {
  position: sticky;
  top: 0;
  z-index: map-get($zindex, 'header');
  background-color: var(--white);
  box-shadow: 0 convert-px-to-rem(1) convert-px-to-rem(4) rgba(0, 0, 0, 0.08);
}

.desktop-header--assoc {
  padding-top: var(--assoc-banner-height, convert-px-to-rem(40));

  :deep(.customer-association-banner) {
    z-index: map-get($zindex, 'header') + 1;
  }
}

.desktop-header__content {
  padding: $pad-2 $pad-3;
}

.desktop-header__row {
  display: flex;
  align-items: center;
  gap: $pad-3;
}

.desktop-header__brand-image {
  display: block;
}

.desktop-header__brand-image--favicon {
  object-fit: cover;
  border: 1px solid var(--gray-300, $gray-300);
  background-color: var(--white);
}

.desktop-header__brand-image--logo {
  width: auto;
  max-width: convert-px-to-rem(160);
  max-height: convert-px-to-rem(42);
  height: auto;
  border-radius: 0;
  border: 0;
  object-fit: contain;
  background-color: transparent;
}

.desktop-header__store {
  flex-shrink: 0;
  border: 0.5px solid var(--dark-lighter-5);
  border-radius: convert-px-to-rem(999);
  background-color: var(--white);
  min-height: convert-px-to-rem(40);
  padding: $pad-2 $pad-3;
  display: inline-grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: $pad-2;
  text-align: left;
  white-space: nowrap;
}

.desktop-header__store-icon,
.desktop-header__chevron {
  color: var(--primary, $primary);
}

.desktop-header__store-label {
  min-width: 0;
  font-size: $font-size-base;
  line-height: 1.2;
  color: var(--body-color, $body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desktop-header__search {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 3rem;
}

.desktop-header__profile {
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 4.25rem;
  padding: 0.375rem 0.75rem;
  color: var(--body-color, $body-color);
  border-radius: 0.375rem;
}

.desktop-header__profile--assoc {
  flex-direction: row;
  gap: 0.5rem;
  background-color: var(--primary-lighter-9, var(--primary));
}

.desktop-header__profile-icon {
  font-size: $font-size-xl;
  color: var(--primary, $primary);
}

.desktop-header__cart {
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: convert-px-to-rem(60);
  padding: 0;
  color: var(--body-color, $body-color);
}

.desktop-header__cart-icon {
  color: var(--primary, $primary);
  width: convert-px-to-rem(20);
  height: convert-px-to-rem(18);
}

.desktop-header__cart-count {
  margin-top: convert-px-to-rem(2);
  font-size: $font-size-base;
  line-height: 1.2;
  white-space: nowrap;
}
</style>
